import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import api from '../../lib/api';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { googleUrlPlaceholder } from '../../lib/googleWorkspace';
import type { GoogleWorkspaceTemplates } from '../../lib/googleWorkspace';

export function GoogleWorkspaceTemplatesCard() {
  const [templates, setTemplates] = useState<GoogleWorkspaceTemplates>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get<{ templates: GoogleWorkspaceTemplates }>('/google-workspace/templates');
        setTemplates(res.data.templates ?? {});
      } catch {
        setMessage('Failed to load templates');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await api.patch<{ templates: GoogleWorkspaceTemplates }>('/google-workspace/templates', {
        templates,
      });
      setTemplates(res.data.templates ?? {});
      setMessage('Templates saved.');
    } catch {
      setMessage('Failed to save templates');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Shared Drive templates</CardTitle>
        <CardDescription>
          Default Google URLs applied to new campaigns. Use &quot;Apply templates&quot; on create job or
          campaign brief tab.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium mb-1">Brief Google Doc template</label>
              <Input
                placeholder={googleUrlPlaceholder('document')}
                value={templates.briefDocUrl || ''}
                onChange={(e) => setTemplates((t) => ({ ...t, briefDocUrl: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Storyboard Slides template</label>
              <Input
                placeholder={googleUrlPlaceholder('presentation')}
                value={templates.storyboardSlidesUrl || ''}
                onChange={(e) => setTemplates((t) => ({ ...t, storyboardSlidesUrl: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Campaign Drive folder template</label>
              <Input
                placeholder={googleUrlPlaceholder('folder')}
                value={templates.campaignFolderUrl || ''}
                onChange={(e) => setTemplates((t) => ({ ...t, campaignFolderUrl: e.target.value }))}
              />
            </div>
            <Button onClick={() => void handleSave()} disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save templates
            </Button>
            {message && <p className="text-sm text-gray-600">{message}</p>}
          </>
        )}
      </CardContent>
    </Card>
  );
}
